import ApiErrorBanner from '@/components/ApiErrorBanner';
import DatePickerNative from '@/components/UI/DatePickerNative/DatePickerNative';
import { errorToast, successToast } from '@/helpers';
import { _isBlank, _isNotBlank, _notNil } from '@/littledash';
import type { Animal } from '@/model/Animal.model';
import type { ISODate } from '@/model/Common.model';
import type { StudyApiId } from '@/model/Study.model';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { RouteService } from '@/support/RouteService';
import { featuresSelector } from '@/support/Selectors';
import { DateUtils } from '@/utils/Date.utils';
import { modalAction, ModalActions, ModalContainer, ModalHeader } from '@/utils/modal';
import { ErrorMessage } from '@hookform/error-message';
import { createSelector } from '@reduxjs/toolkit';
import { useMemo, type FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

const reasonForChangeSelector = createSelector([featuresSelector], (features) => ({
  reasonForChangeEnabled: features?.reason_for_change === true,
}));

interface SetTrackingDateForm {
  trackingDate: ISODate;
}

interface SetTrackingDateProps {
  subjects: Array<Animal>;
  studyApiId: StudyApiId;
  reasonForChangeOverride?: string;
  handleCallback: () => void;
  closeModal: () => void;
}

const SetTrackingDate: FC<SetTrackingDateProps> = ({
  subjects,
  studyApiId,
  handleCallback,
  closeModal,
  reasonForChangeOverride,
}) => {
  const defaultDate = useMemo(() => {
    if (subjects.length === 1 && _isNotBlank(subjects[0]?.tracking_started_at)) {
      return subjects[0].tracking_started_at as ISODate;
    }
    return DateUtils.dateNow();
  }, [subjects]);

  const allSubjectsMissingTrackingDate = useMemo(
    () => subjects.every(({ tracking_started_at }) => _isBlank(tracking_started_at)),
    [subjects]
  );

  const { reasonForChangeEnabled } = useSelector(reasonForChangeSelector);
  const dispatch = useDispatch();
  const { openModal } = modalAction(dispatch);

  const {
    handleSubmit,
    formState: { errors, isSubmitting },
    control,
  } = useForm<SetTrackingDateForm>({
    mode: 'onChange',
    defaultValues: { trackingDate: defaultDate },
  });

  const {
    invoke: updateTrackingDates,
    loading: updatingTrackingDates,
    response: updateResponse,
  } = useApiHook({
    endpoint: 'PATCH /api/v1/studies/{studyApiId}/animals/tracking-date',
    invokeOnInit: false,
    options: { onError: { throw: false, capture: true, toast: false, slug: 'animal-tracking-date-update' } },
  });

  const saveTrackingDates = async (trackingDate: ISODate, reasonForChange?: string) => {
    const animalApiIds = subjects.map(({ api_id }) => api_id!);
    const result = await updateTrackingDates({
      path: { studyApiId },
      body: {
        animal_api_ids: animalApiIds,
        tracking_started_at: trackingDate,
        ...(_notNil(reasonForChange) ? { reason_for_change: reasonForChange } : {}),
      },
    });
    if (result.type === 'success') {
      successToast(`Set ${subjects.length} tracking date${subjects.length !== 1 ? 's' : ''} `);
      handleCallback();
    } else if (reasonForChangeEnabled) {
      errorToast('There was a problem setting the tracking date');
    }
  };

  const onSubmit = async ({ trackingDate }: SetTrackingDateForm) => {
    if (reasonForChangeEnabled && !allSubjectsMissingTrackingDate) {
      if (_isNotBlank(reasonForChangeOverride)) {
        await saveTrackingDates(trackingDate, reasonForChangeOverride);
        return;
      }
      openModal('BULK_REASON_FOR_CHANGE', {
        title: 'Animal information has changed',
        onClick: (reasonForChange?: string) => saveTrackingDates(trackingDate, reasonForChange),
      });
      return;
    }
    await saveTrackingDates(trackingDate);
  };

  const submitting = isSubmitting || updatingTrackingDates;

  return (
    <ModalContainer size="narrow" className="bg-white">
      <form data-test-component="SetTrackingDate" data-test-element="form" onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-column bb b--moon-gray">
          <ModalHeader title="Set a tracking date" className="pl3 pt3 pb2 w-100" />
          <div className="f6 lh-copy pl3 pb2">
            <p>Choose a date when you&apos;d like to begin graphing measurements.</p>
            <a
              href={RouteService.help('tracking_date')}
              target="_blank"
              rel="noopener noreferrer"
              className="link blue"
            >
              Read more
            </a>
          </div>
        </div>
        {updateResponse?.type === 'error' && <ApiErrorBanner error={updateResponse.error} />}
        <div className="pl3" style={{ width: 290 }} data-test-element="body">
          <Controller
            name="trackingDate"
            control={control}
            rules={{ required: 'This field is required' }}
            render={({ field: { onChange, value } }) => (
              <div className="pt3">
                <label htmlFor="trackingDate">Tracking date</label>
                <DatePickerNative id="trackingDate" value={value} onChange={onChange} />
              </div>
            )}
          />
          <ErrorMessage
            errors={errors}
            name="trackingDate"
            render={({ message }) => (
              <p className="f6 red db mb3" data-test-element="error-message">
                {message}
              </p>
            )}
          />
        </div>
        <ModalActions
          className="pa3 bt b--moon-gray"
          onSubmit={handleSubmit(onSubmit)}
          onCancel={closeModal}
          submitBtnText="Save"
          cancelBtnText="Cancel"
          submitButtonProps={{ disabled: submitting || _notNil(errors.trackingDate), loading: submitting }}
          cancelButtonProps={{ disabled: submitting }}
        />
      </form>
    </ModalContainer>
  );
};

export default SetTrackingDate;
